import { useEffect, useState } from 'react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/hooks/useAuth';
import { useLoginTracker } from '@/hooks/useLoginTracker';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { useDailyBonus } from '@/hooks/useDailyBonus';
import { AvatarOnboarding } from './AvatarOnboarding';
import { LocationConsentDialog } from './LocationConsentDialog';
import { DailyLoginBonus } from './DailyLoginBonus';

interface AuthenticatedAppProps {
  children: React.ReactNode;
}

export const AuthenticatedApp = ({ children }: AuthenticatedAppProps) => {
  const { user } = useAuth();
  const { profile, loading } = useUserProfile();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDailyBonus, setShowDailyBonus] = useState(false);
  
  // Track login sessions for admin analytics
  useLoginTracker();
  
  // Monitor session timeout and show warnings
  useSessionTimeout();

  // Daily login bonus system
  const { 
    canClaim, 
    streakDay, 
    currentStreak, 
    longestStreak, 
    todayReward, 
    claimBonus, 
    claiming,
    loading: bonusLoading 
  } = useDailyBonus();

  useEffect(() => {
    if (!loading && user && profile) {
      // Check if user needs onboarding - needs both username AND avatar config
      const hasUsername = profile.username && profile.username.length > 0;
      const hasValidAvatar = profile.avatar_config && 
        typeof profile.avatar_config === 'object' && 
        Object.keys(profile.avatar_config).length > 0;
      
      const needsOnboarding = !hasUsername || !hasValidAvatar;
      
      if (needsOnboarding) {
        setShowOnboarding(true);
      } else {
        setShowOnboarding(false);
      }
    }
  }, [user, profile, loading]);

  // Show daily bonus after onboarding is complete
  useEffect(() => {
    if (!loading && !bonusLoading && user && !showOnboarding && canClaim) {
      // Small delay to ensure smooth transition after onboarding
      const timer = setTimeout(() => {
        setShowDailyBonus(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, loading, bonusLoading, showOnboarding, canClaim]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <>{children}</>;
  }

  if (showOnboarding) {
    return (
      <AvatarOnboarding
        onComplete={() => setShowOnboarding(false)}
        onSkip={() => setShowOnboarding(false)}
      />
    );
  }

  const handleClaimBonus = async () => {
    await claimBonus();
    setShowDailyBonus(false);
  };

  return (
    <>
      <LocationConsentDialog />
      <DailyLoginBonus
        open={showDailyBonus}
        onOpenChange={setShowDailyBonus}
        streakDay={streakDay}
        currentStreak={currentStreak}
        longestStreak={longestStreak}
        reward={todayReward}
        onClaim={handleClaimBonus}
        claiming={claiming}
      />
      {children}
    </>
  );
};